import { Injectable } from '@angular/core';
import { defaultUiLanguage, toSupportedUiLanguage } from '../models/language.model';

/**
 * Drží jazyk, ve kterém aplikace právě běží. Nic víc.
 *
 * Existuje kvůli `LanguageInterceptor`, který jazyk potřebuje na každém requestu.
 * Nesmí mít **žádnou závislost** — kdyby si sáhla na `TranslateService`, vznikne cyklus
 * v DI: první HTTP request aplikace je stažení překladů, takže by se `HTTP_INTERCEPTORS`
 * resolvovaly znovu, ještě než se doresolvují (`NG0200`).
 *
 * Zapisuje sem výhradně `LanguageService`, která k tomu doplní `translate.use()`
 * a ng-zorro locale.
 *
 * Volba přežívá zavření prohlížeče: než se uživatel přihlásí, aplikace o něm nic neví
 * a jinak by se řídila jazykem operačního systému — Čech na anglických Windows by tak
 * dostal anglický login.
 *
 * Volba patří **zařízení, ne účtu**. Na sdíleném počítači ji další uživatel přepíše
 * vlaječkou, která je na každé obrazovce. Je to záměrný rozdíl oproti motivu:
 * `ThemeService.applyFromAccount()` localStorage účtem přepisuje, protože `User.Theme`
 * existuje kvůli vzhledu. `User.LanguageIso` naproti tomu řídí jazyk e-mailů, ne rozhraní,
 * takže tady vyhrává zařízení. Nesjednocuj to — rozbije se jedno nebo druhé.
 */
@Injectable({ providedIn: 'root' })
export class LanguageStateService {

  private static readonly storageKey = 'uiLanguage';

  /**
   * Jazyk, který si uživatel vybral sám. Vlastní klíč, ne příznak nad `storageKey` —
   * do toho zapisuje každé nastavení včetně odvozeného, takže by se s volbou rozešel:
   * po prokliku z e-mailu s `?lang=` by hodnota patřila odkazu, ale tvářila se jako volba.
   */
  private static readonly choiceKey = 'uiLanguageChosen';

  // Interceptor posílá Accept-Language už na prvním requestu (UserInfo z APP_INITIALIZERu),
  // který běží dřív, než AppComponent stihne jazyk nastavit. Bez tohohle by úvodní volání
  // šla vždycky česky, i když má uživatel na zařízení angličtinu.
  private currentLanguage: string = this.chosen ?? this.remembered ?? defaultUiLanguage;

  get current(): string {
    return this.currentLanguage;
  }

  /**
   * Naposledy použitý jazyk na tomhle počítači, nebo undefined, když tu ještě nikdo nebyl.
   * Není to volba uživatele — může pocházet i z profilu nebo z odkazu v e-mailu.
   */
  get remembered(): string | undefined {
    const stored = localStorage.getItem(LanguageStateService.storageKey);

    return stored && stored === toSupportedUiLanguage(stored) ? stored : undefined;
  }

  /** Jazyk, který si uživatel na tomhle počítači vybral sám. */
  get chosen(): string | undefined {
    const stored = localStorage.getItem(LanguageStateService.choiceKey);

    return stored && stored === toSupportedUiLanguage(stored) ? stored : undefined;
  }

  /** Nepodporovaná nebo prázdná hodnota spadne na výchozí jazyk. */
  set(languageIso: string | undefined | null): string {
    this.currentLanguage = toSupportedUiLanguage(languageIso);
    localStorage.setItem(LanguageStateService.storageKey, this.currentLanguage);

    return this.currentLanguage;
  }

  /** Ruční volba uživatele. Platí nad jazykem z profilu i z odkazu a přežije odhlášení. */
  choose(languageIso: string): string {
    const language = this.set(languageIso);
    localStorage.setItem(LanguageStateService.choiceKey, language);

    return language;
  }
}
